import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { currencyFormatter } from 'components/helpers/currency-formatter';
import { useIsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import HistoryDateTimeUpdateDialog from 'features/settings/history/components/HistoryDateTimeUpdateDialog';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { baseCell, dateTimeCell, DataGridCell } from 'components/cells';
import { filterOperators, numberFilterOperators } from 'components/enhanced';
import { ConfirmationDeleteTableDialog } from 'components/ui/feedback';

export const EquipmentHistoryLineColumns = (
  handleDateTimeUpdate: any,
  handleDelete: any,
  hideFields: boolean
): GridColDef[] => {
  const translationPrefix = 'entities.equipment.history';

  const { t } = useTranslation();
  const columns: GridColDef[] = [];
  const isAdmin = useIsTenantAdministrator();

  columns.push(
    {
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        const actions = [];
        if (isAdmin) {
          actions.push(
            <HistoryDateTimeUpdateDialog
              key={980}
              id={params.row['id']}
              handleUpdate={handleDateTimeUpdate}
            />
          );
          actions.push(
            <ConfirmationDeleteTableDialog handleDelete={handleDelete} id={params.row.id} />
          );
        }

        return actions;
      }
    } as GridColDef,
    {
      field: 'equipment_code',
      headerName: t(`${translationPrefix}.equipment`),
      width: 100,
      filterOperators,
      editable: false,
      renderCell: baseCell
    },
    {
      field: 'datetime',
      headerName: t(`${translationPrefix}.datetime`),
      width: 150,
      filterOperators,
      renderCell: dateTimeCell,
      valueFormatter: (params) => {
        return params.value ? dayjs(params.value).format('YYYY-MM-DD HH:MM:00') : '';
      }
    },
    {
      field: 'contract_header',
      headerName: t(`${translationPrefix}.contract_header`),
      width: 100,
      filterOperators,
      renderCell: baseCell
    }
  );

  ['purchase_amount', 'yearly_lease_amount', 'yearly_maintenance_amount'].forEach((field) => {
    columns.push({
      field: field,
      width: 150,
      headerName: t(`${translationPrefix}.${field}`),
      filterOperators: numberFilterOperators,
      renderCell: (params: GridRenderCellParams) => {
        return <DataGridCell content={currencyFormatter(params.value)} />;
      }
    });
  });

  if (!hideFields) {
    [
      'exams_snapshot',
      'laboratories_snapshot',
      'operational_units_snapshot',
      'workstations_snapshot',
      'sectors_snapshot'
    ].forEach((field) => {
      columns.push({
        field: field,
        width: 200,
        headerName: t(`${translationPrefix}.${field}`),
        sortable: false,
        valueFormatter: ({ value }) => {
          if (!Array.isArray(value)) return '';
          const values = value.map((item) => item.code);
          return values.join(', ');
        },
        filterOperators: filterOperators,
        renderCell: (params: GridRenderCellParams) => {
          const value = Array.isArray(params.value) ? params.value.map((item) => item.code) : [];
          return <DataGridCell content={value.length ? value.join(', ') : ''} />;
        }
      });
    });
  }

  return columns;
};
